import { queryOptions } from '@tanstack/react-query';
import { api, apiMutation, mutation } from '../../../api/http';

export interface ArticleListItemResponse {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  status: 'Draft' | 'Published' | 'Archived';
  version: number;
  updatedAt: string;
}

export interface ArticleListResponse {
  items: ArticleListItemResponse[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ArticleDraftResponse {
  id: string;
  slug: string;
  status: 'Draft' | 'Published' | 'Archived';
  version: number;
  title: string;
  excerpt: string;
  bodyMarkdown: string;
  coverMediaId: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  tags: ArticleTagResponse[];
  seriesId: string | null;
  seriesPosition: number | null;
  revisionNumber: number;
  updatedAt: string;
}

export interface ArticlePreviewResponse {
  id: string;
  slug: string;
  status: 'Draft' | 'Published' | 'Archived';
  title: string;
  excerpt: string;
  bodyHtml: string;
  revisionNumber: number;
  updatedAt: string;
  coverUrl: string | null;
  coverAlt: string | null;
}

export interface ArticleEditorValues {
  title: string;
  slug: string;
  excerpt: string;
  bodyMarkdown: string;
  coverMediaId: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  tagIds: string[];
  seriesId: string | null;
  seriesPosition: number | null;
}

export interface ArticleTagResponse {
  id: string;
  name: string;
  slug: string;
}

export type CreateArticleSubmission = ArticleEditorValues;

export interface SaveArticleSubmission extends ArticleEditorValues {
  expectedVersion: number;
}

export interface ArticlePublicationSubmission {
  expectedVersion: number;
}

export interface ArticleScheduleResponse {
  id: string;
  articleId: string;
  targetRevisionId: string;
  targetRevisionNumber: number;
  dueAt: string;
  status: string;
}

export interface ArticleScheduleSubmission {
  expectedVersion: number;
  dueAt: string;
}

export interface ArticleRevisionHistoryItem {
  revisionNumber: number;
  createdAt: string;
  note: string | null;
  author: string;
  title: string;
  isWorking: boolean;
  isPublished: boolean;
}

export interface ArticleRevisionSnapshot {
  revisionNumber: number;
  createdAt: string;
  note: string | null;
  author: string;
  title: string;
  excerpt: string;
  bodyMarkdown: string;
  coverMediaId: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  tags: ArticleTagResponse[];
}

export interface RestoreArticleRevisionSubmission {
  expectedVersion: number;
  note: string | null;
}

export interface ArticleListFilter {
  status: string;
  query: string;
  page: number;
}

export const articleQueryKeys = {
  all: ['internal', 'articles'] as const,
  list: (filter: ArticleListFilter) => ['internal', 'articles', filter] as const,
  picker: ['internal', 'articles', 'picker'] as const,
  draft: (id: string) => ['internal', 'articles', id] as const,
  preview: (id: string) => ['internal', 'articles', id, 'preview'] as const,
  revisions: (id: string) => ['internal', 'articles', id, 'revisions'] as const,
  revision: (id: string, revisionNumber: number | null) =>
    ['internal', 'articles', id, 'revisions', revisionNumber] as const,
  schedule: (id: string) => ['internal', 'articles', id, 'schedule'] as const,
};

export const articleListQuery = (filter: ArticleListFilter) =>
  queryOptions({
    queryKey: articleQueryKeys.list(filter),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ page: String(filter.page), pageSize: '15' });
      if (filter.status) params.set('status', filter.status);
      if (filter.query) params.set('q', filter.query);
      return api<ArticleListResponse>(`/internal/articles?${params}`, { signal });
    },
  });

export const articlePickerQuery = queryOptions({
  queryKey: articleQueryKeys.picker,
  queryFn: ({ signal }) =>
    api<ArticleListResponse>('/internal/articles?page=1&pageSize=100', { signal }),
});

export const articleDraftQuery = (id: string) =>
  queryOptions({
    queryKey: articleQueryKeys.draft(id),
    queryFn: ({ signal }) => api<ArticleDraftResponse>(`/internal/articles/${id}`, { signal }),
  });

export const articlePreviewQuery = (id: string) =>
  queryOptions({
    queryKey: articleQueryKeys.preview(id),
    queryFn: ({ signal }) =>
      api<ArticlePreviewResponse>(`/internal/articles/${id}/preview`, {
        signal,
        cache: 'no-store',
      }),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

export const articleRevisionHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: articleQueryKeys.revisions(id),
    queryFn: ({ signal }) =>
      api<ArticleRevisionHistoryItem[]>(`/internal/articles/${id}/revisions`, { signal }),
  });

export const articleRevisionQuery = (id: string, revisionNumber: number | null) =>
  queryOptions({
    queryKey: articleQueryKeys.revision(id, revisionNumber),
    queryFn: ({ signal }) =>
      api<ArticleRevisionSnapshot>(`/internal/articles/${id}/revisions/${revisionNumber}`, {
        signal,
      }),
    enabled: revisionNumber !== null,
  });

export const articleScheduleQuery = (id: string) =>
  queryOptions({
    queryKey: articleQueryKeys.schedule(id),
    queryFn: ({ signal }) =>
      api<ArticleScheduleResponse | null>(`/internal/articles/${id}/schedule`, { signal }),
  });

export function createArticle(submission: CreateArticleSubmission) {
  return mutation<ArticleDraftResponse>('/internal/articles', submission);
}

export function saveArticle(id: string, submission: SaveArticleSubmission, signal?: AbortSignal) {
  return apiMutation<ArticleDraftResponse>(`/internal/articles/${id}`, 'PUT', submission, signal);
}

export async function changeArticlePublication(
  id: string,
  action: 'publish' | 'unpublish',
  submission: ArticlePublicationSubmission,
) {
  return mutation<ArticleDraftResponse>(`/internal/articles/${id}/${action}`, submission);
}

export function archiveArticle(id: string, submission: ArticlePublicationSubmission) {
  return apiMutation<ArticleDraftResponse>(`/internal/articles/${id}/archive`, 'POST', submission);
}

export function scheduleArticlePublication(id: string, submission: ArticleScheduleSubmission) {
  return mutation<ArticleScheduleResponse>(`/internal/articles/${id}/schedule`, submission);
}

export function cancelArticlePublicationSchedule(id: string, scheduleId: string) {
  return apiMutation<void>(`/internal/articles/${id}/schedule/${scheduleId}`, 'DELETE');
}

export function restoreArticleRevision(
  id: string,
  revisionNumber: number,
  submission: RestoreArticleRevisionSubmission,
) {
  return mutation<ArticleDraftResponse>(
    `/internal/articles/${id}/revisions/${revisionNumber}/restore`,
    submission,
  );
}
